import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { Plus, Trash2, Edit2, Calendar, Clock, Tag, Layers, AlertTriangle } from 'lucide-react';
import { formatVND } from '../../utils/formatters';
import { flashSaleService } from '../../components/services/FlashsaleService';

const MAX_CAMPAIGNS = 3;
const MAX_PRODUCTS_PER_CAMPAIGN = 3;

export default function FlashSaleAdmin() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    id: null,
    title: '',
    startTime: '',
    endTime: '',
    status: 'ACTIVE'
  });

  const [showProductModal, setShowProductModal] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    id: null,
    productId: '',
    name: '',
    image: '',
    flashSalePrice: '',
    originalPrice: '',
    stockLimit: '',
    maxStock: 0
  });

  // ─── Fetch helpers ───────────────────────────────────────────────
  const fetchCampaignProducts = useCallback(async (campaign) => {
    if (!campaign) return;
    try {
      const response = await flashSaleService.getCampaignProducts(campaign.id);
      const list = response.data || response;
      setProducts(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Lỗi tải sản phẩm của chiến dịch:', error);
      setProducts([]);
    }
  }, []);

  const fetchCampaigns = useCallback(async (currentSelected) => {
    try {
      const response = await flashSaleService.getCampaigns();
      const list = response.data || response;
      const arr = Array.isArray(list) ? list : [];
      setCampaigns(arr);

      if (arr.length > 0) {
        const stillExists = currentSelected
          ? arr.find((c) => c.id === currentSelected.id)
          : null;
        const target = stillExists || arr[0];
        setSelectedCampaign(target);
        await fetchCampaignProducts(target);
      } else {
        setSelectedCampaign(null);
        setProducts([]);
      }
    } catch (error) {
      console.error('Lỗi tải danh sách chiến dịch:', error);
    }
  }, [fetchCampaignProducts]);

  const fetchAllProducts = async () => {
    try {
      const response = await flashSaleService.getAllProducts();
      const rawData = response.data || response;
      let productList = [];
      if (Array.isArray(rawData)) {
        productList = rawData;
      } else if (rawData?.data && Array.isArray(rawData.data)) {
        productList = rawData.data;
      } else if (rawData?.data?.data && Array.isArray(rawData.data.data)) {
        productList = rawData.data.data;
      }
      setAllProducts(productList);
    } catch (error) {
      console.error('Lỗi tải danh sách sản phẩm gốc:', error);
      setAllProducts([]);
    }
  };

  useEffect(() => {
    fetchCampaigns(null);
    fetchAllProducts();
  }, [fetchCampaigns]);

  // ─── Chọn chiến dịch ──────────────────────────────────────────────
  const handleSelectCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    await fetchCampaignProducts(campaign);
  };

  // ─── Chọn sản phẩm từ dropdown ────────────────────────────────────
  const handleProductSelectChange = (e) => {
    const selectedId = e.target.value;
    const foundProduct = allProducts.find(
      (p) => (p.realId || p.productId || p.id).toString() === selectedId
    );
    if (foundProduct) {
      const stock =
        foundProduct.stock !== undefined
          ? foundProduct.stock
          : foundProduct.stock_quantity !== undefined
          ? foundProduct.stock_quantity
          : foundProduct.quantity !== undefined
          ? foundProduct.quantity
          : 0;
      setProductForm((prev) => ({
        ...prev,
        productId: foundProduct.realId || foundProduct.productId || foundProduct.id,
        name: foundProduct.name || '',
        image: foundProduct.thumbnail || foundProduct.image || '',
        originalPrice: foundProduct.price || 0,
        maxStock: stock,
        stockLimit: ''
      }));
    }
  };

  // ─── Lưu chiến dịch ───────────────────────────────────────────────
  const handleSaveCampaign = async (e) => {
    e.preventDefault();

    if (!isEditing && campaigns.length >= MAX_CAMPAIGNS) {
      Swal.fire({
        title: 'Đã đạt giới hạn!',
        text: `Hệ thống chỉ cho phép tối đa ${MAX_CAMPAIGNS} chiến dịch Flash Sale!`,
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }

    try {
      if (isEditing) {
        await flashSaleService.updateCampaign(campaignForm.id, campaignForm);
        Swal.fire({
          title: 'Thành công!',
          text: 'Cập nhật chiến dịch thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      } else {
        await flashSaleService.createCampaign(campaignForm);
        Swal.fire({
          title: 'Thành công!',
          text: 'Tạo chiến dịch mới thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
      setShowCampaignModal(false);
      resetCampaignForm();
      await fetchCampaigns(selectedCampaign);
    } catch (error) {
      console.error('Lỗi lưu chiến dịch:', error);
      Swal.fire({
        title: 'Thất bại!',
        text: 'Lỗi khi lưu chiến dịch! Vui lòng thử lại.',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    }
  };

  const handleDeleteCampaign = async (id) => {
    const result = await Swal.fire({
      title: 'Xác nhận xóa?',
      text: "Bạn có chắc muốn xóa chiến dịch này không?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e11d48',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Xóa ngay',
      cancelButtonText: 'Hủy',
      background: '#0f172a',
      color: '#ffffff'
    });

    if (result.isConfirmed) {
      try {
        await flashSaleService.deleteCampaign(id);
        Swal.fire({
          title: 'Đã xóa!',
          text: 'Đã xóa chiến dịch thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
        await fetchCampaigns(null);
      } catch (error) {
        console.error('Lỗi xóa chiến dịch:', error);
        Swal.fire({
          title: 'Thất bại!',
          text: 'Xóa chiến dịch thất bại!',
          icon: 'error',
          confirmButtonColor: '#e11d48',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({ id: null, title: '', startTime: '', endTime: '', status: 'ACTIVE' });
    setIsEditing(false);
  };

  // ─── Thêm / Sửa sản phẩm Flash Sale ──────────────────────────────
  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!selectedCampaign) return;

    if (!isEditingProduct && products.length >= MAX_PRODUCTS_PER_CAMPAIGN) {
      Swal.fire({
        title: 'Đã đạt giới hạn!',
        text: `Mỗi chiến dịch Flash Sale chỉ được phép tối đa ${MAX_PRODUCTS_PER_CAMPAIGN} sản phẩm!`,
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }

    const limit = Number(productForm.stockLimit);
    const stock = Number(productForm.maxStock);

    if (limit <= 0) {
      Swal.fire({
        title: 'Không hợp lệ!',
        text: 'Số lượng Flash Sale phải lớn hơn 0!',
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }
    if (limit > stock) {
      Swal.fire({
        title: 'Vượt quá tồn kho!',
        text: `Số lượng Flash Sale (${limit}) không được lớn hơn tồn kho hiện tại (${stock})!`,
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }

    try {
      if (isEditingProduct) {
        await flashSaleService.updateProductInCampaign(productForm.id, productForm);
        Swal.fire({
          title: 'Thành công!',
          text: 'Cập nhật sản phẩm Flash Sale thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      } else {
        await flashSaleService.addProductToCampaign(selectedCampaign.id, productForm);
        Swal.fire({
          title: 'Thành công!',
          text: 'Thêm sản phẩm vào Flash Sale thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
      setShowProductModal(false);
      resetProductForm();
      await fetchCampaignProducts(selectedCampaign);
    } catch (error) {
      console.error('Lỗi lưu sản phẩm flash sale:', error);
      Swal.fire({
        title: 'Thất bại!',
        text: isEditingProduct ? 'Lỗi khi cập nhật sản phẩm!' : 'Lỗi khi thêm sản phẩm!',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    const result = await Swal.fire({
      title: 'Xác nhận gỡ?',
      text: "Bạn có muốn gỡ sản phẩm này khỏi Flash Sale?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e11d48',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Gỡ ngay',
      cancelButtonText: 'Hủy',
      background: '#0f172a',
      color: '#ffffff'
    });

    if (result.isConfirmed) {
      try {
        await flashSaleService.removeProductFromCampaign(productId);
        Swal.fire({
          title: 'Đã gỡ!',
          text: 'Đã gỡ sản phẩm thành công!',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
        await fetchCampaignProducts(selectedCampaign);
      } catch (error) {
        console.error('Lỗi xóa sản phẩm flash sale:', error);
        Swal.fire({
          title: 'Thất bại!',
          text: 'Gỡ sản phẩm thất bại!',
          icon: 'error',
          confirmButtonColor: '#e11d48',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
    }
  };

  const resetProductForm = () => {
    setProductForm({
      id: null,
      productId: '',
      name: '',
      image: '',
      flashSalePrice: '',
      originalPrice: '',
      stockLimit: '',
      maxStock: 0
    });
    setIsEditingProduct(false);
  };

  const campaignLimitReached = campaigns.length >= MAX_CAMPAIGNS;
  const productLimitReached = products.length >= MAX_PRODUCTS_PER_CAMPAIGN;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header Quản Trị */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800/80 backdrop-blur-xl">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
              Quản trị hệ thống
            </span>
            <h1 className="text-2xl sm:text-3xl font-extrabold font-serif text-white mt-2 flex items-center gap-2">
              <Layers className="text-amber-400 w-7 h-7" /> Quản Lý Hệ Thống Flash Sale
            </h1>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => {
                if (campaignLimitReached) {
                  Swal.fire({
                    title: 'Đã đạt giới hạn!',
                    text: `Hệ thống chỉ cho phép tối đa ${MAX_CAMPAIGNS} chiến dịch Flash Sale!`,
                    icon: 'warning',
                    confirmButtonColor: '#e11d48',
                    background: '#0f172a',
                    color: '#ffffff'
                  });
                  return;
                }
                resetCampaignForm();
                setShowCampaignModal(true);
              }}
              disabled={campaignLimitReached}
              className={`px-5 py-3 rounded-2xl font-bold flex items-center gap-2 transition text-xs shadow-lg ${
                campaignLimitReached
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed shadow-none'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-950 cursor-pointer shadow-amber-500/25'
              }`}
            >
              <Plus size={16} /> Tạo Chiến Dịch Mới
            </button>
            {campaignLimitReached && (
              <span className="flex items-center gap-1 text-[11px] text-amber-400 font-semibold">
                <AlertTriangle size={12} /> Đã đạt giới hạn {MAX_CAMPAIGNS} chiến dịch
              </span>
            )}
          </div>
        </div>

        {/* Danh sách các Chiến Dịch */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {campaigns.map((camp) => (
            <div
              key={camp.id}
              onClick={() => handleSelectCampaign(camp)}
              className={`p-6 rounded-3xl border cursor-pointer transition flex flex-col justify-between space-y-4 shadow-xl ${
                selectedCampaign?.id === camp.id
                  ? 'bg-indigo-950/40 border-indigo-500 shadow-indigo-500/10'
                  : 'bg-slate-900 border-slate-800/80 hover:border-slate-700'
              }`}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase border ${
                    camp.status === 'ACTIVE'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  }`}>
                    {camp.status}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCampaignForm(camp);
                        setIsEditing(true);
                        setShowCampaignModal(true);
                      }}
                      className="p-2 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-xl transition cursor-pointer"
                      title="Chỉnh sửa chiến dịch"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCampaign(camp.id);
                      }}
                      className="p-2 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 rounded-xl transition cursor-pointer"
                      title="Xóa chiến dịch"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-lg text-white">{camp.title}</h3>
              </div>

              <div className="space-y-1.5 text-xs text-slate-400 border-t border-slate-800 pt-4">
                <div className="flex items-center gap-2">
                  <Calendar size={13} className="text-amber-400" />
                  Bắt đầu: {new Date(camp.startTime).toLocaleString('vi-VN')}
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={13} className="text-rose-400" />
                  Kết thúc: {new Date(camp.endTime).toLocaleString('vi-VN')}
                </div>
              </div>
            </div>
          ))}

          {/* Placeholder slots trống */}
          {Array.from({ length: MAX_CAMPAIGNS - campaigns.length }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="p-6 rounded-3xl border border-dashed border-slate-800 bg-slate-900/40 flex items-center justify-center text-slate-600 text-xs font-bold min-h-[140px]"
            >
              Slot trống ({campaigns.length + i + 1}/{MAX_CAMPAIGNS})
            </div>
          ))}
        </div>

        {/* Khu vực Quản lý Sản phẩm trong Chiến dịch đang chọn */}
        {selectedCampaign && (
          <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800/80 space-y-6 shadow-2xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-5">
              <div>
                <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2 font-serif">
                  <Tag size={18} /> Sản phẩm trong chiến dịch: "{selectedCampaign.title}"
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Danh sách mặt hàng giảm giá sốc.&nbsp;
                  <span className={`font-bold ${productLimitReached ? 'text-rose-400' : 'text-emerald-400'}`}>
                    ({products.length}/{MAX_PRODUCTS_PER_CAMPAIGN} sản phẩm)
                  </span>
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={() => {
                    if (productLimitReached) {
                      Swal.fire({
                        title: 'Đã đạt giới hạn!',
                        text: `Mỗi chiến dịch chỉ được tối đa ${MAX_PRODUCTS_PER_CAMPAIGN} sản phẩm!`,
                        icon: 'warning',
                        confirmButtonColor: '#e11d48',
                        background: '#0f172a',
                        color: '#ffffff'
                      });
                      return;
                    }
                    resetProductForm();
                    setShowProductModal(true);
                  }}
                  disabled={productLimitReached}
                  className={`px-4 py-2.5 rounded-2xl font-bold flex items-center gap-1.5 text-xs transition shadow-md ${
                    productLimitReached
                      ? 'bg-slate-800 text-slate-500 cursor-not-allowed shadow-none'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer shadow-emerald-600/20'
                  }`}
                >
                  <Plus size={16} /> Thêm Sản Phẩm Vào Flash Sale
                </button>
                {productLimitReached && (
                  <span className="flex items-center gap-1 text-[11px] text-rose-400 font-semibold">
                    <AlertTriangle size={12} /> Đã đạt giới hạn {MAX_PRODUCTS_PER_CAMPAIGN} sản phẩm
                  </span>
                )}
              </div>
            </div>

            {/* Bảng danh sách sản phẩm */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-slate-950/60 uppercase text-slate-400 font-bold border-b border-slate-800 text-[11px]">
                  <tr>
                    <th className="p-4">Sản phẩm</th>
                    <th className="p-4">Giá Flash Sale</th>
                    <th className="p-4">Giá gốc</th>
                    <th className="p-4">Đã bán / Giới hạn</th>
                    <th className="p-4 text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-12 text-slate-500">
                        Chưa có sản phẩm nào trong chiến dịch này.
                      </td>
                    </tr>
                  ) : (
                    products.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-800/40 transition">
                        <td className="p-4 flex items-center gap-3">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-11 h-11 rounded-2xl object-cover bg-slate-950 shrink-0 border border-slate-700/80"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="font-bold text-white line-clamp-1">{item.name}</div>
                        </td>
                        <td className="p-4 font-extrabold text-amber-400 font-mono text-sm">{formatVND(item.flashSalePrice)}</td>
                        <td className="p-4 text-slate-500 line-through font-mono">{formatVND(item.originalPrice)}</td>
                        <td className="p-4 font-mono">
                          <span className="text-emerald-400 font-bold">{item.soldQuantity ?? 0}</span>
                          {' / '}
                          {item.stockLimit}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                const originalProd = allProducts.find(
                                  (p) => (p.realId || p.productId || p.id).toString() === (item.productId || item.realId || item.id).toString()
                                );
                                
                                const realStock = originalProd 
                                  ? (originalProd.stock !== undefined ? originalProd.stock : originalProd.stock_quantity !== undefined ? originalProd.stock_quantity : originalProd.quantity !== undefined ? originalProd.quantity : 9999)
                                  : (item.maxStock !== undefined ? item.maxStock : 9999);

                                setProductForm({
                                  id: item.id,
                                  productId: item.productId || originalProd?.realId || originalProd?.id,
                                  name: item.name || '',
                                  image: item.image || '',
                                  flashSalePrice: item.flashSalePrice || '',
                                  originalPrice: item.originalPrice || '',
                                  stockLimit: item.stockLimit || '',
                                  maxStock: realStock
                                });
                                setIsEditingProduct(true);
                                setShowProductModal(true);
                              }}
                              className="p-2.5 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-2xl transition cursor-pointer"
                              title="Sửa thông tin Flash Sale"
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(item.id)}
                              className="p-2.5 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 rounded-2xl transition cursor-pointer"
                              title="Gỡ sản phẩm"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal Thêm/Sửa Chiến Dịch */}
        {showCampaignModal && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg p-8 space-y-6 shadow-2xl">
              <h3 className="text-lg font-bold text-white font-serif">
                {isEditing ? 'Chỉnh Sửa Chiến Dịch Flash Sale' : `Tạo Chiến Dịch Mới (${campaigns.length + 1}/${MAX_CAMPAIGNS})`}
              </h3>
              <form onSubmit={handleSaveCampaign} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tiêu đề chiến dịch</label>
                  <input
                    type="text"
                    required
                    value={campaignForm.title}
                    onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })}
                    placeholder="Ví dụ: Giờ Vàng Flash Sale Hàng Hiệu"
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-400 transition"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Thời gian bắt đầu</label>
                    <input
                      type="datetime-local"
                      required
                      value={campaignForm.startTime ? campaignForm.startTime.slice(0, 16) : ''}
                      onChange={(e) => setCampaignForm({ ...campaignForm, startTime: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-400 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Thời gian kết thúc</label>
                    <input
                      type="datetime-local"
                      required
                      value={campaignForm.endTime ? campaignForm.endTime.slice(0, 16) : ''}
                      onChange={(e) => setCampaignForm({ ...campaignForm, endTime: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-400 transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Trạng thái</label>
                  <select
                    value={campaignForm.status}
                    onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-400 transition cursor-pointer"
                  >
                    <option value="ACTIVE">ACTIVE (Đang diễn ra)</option>
                    <option value="UPCOMING">UPCOMING (Sắp diễn ra)</option>
                    <option value="ENDED">ENDED (Đã kết thúc)</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => { setShowCampaignModal(false); resetCampaignForm(); }}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold transition cursor-pointer text-slate-300"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition cursor-pointer shadow-lg shadow-amber-500/30"
                  >
                    Lưu Lại
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Thêm / Sửa Sản Phẩm Vào Flash Sale */}
        {showProductModal && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div>
                <h3 className="text-lg font-bold text-white font-serif">
                  {isEditingProduct ? 'Chỉnh Sửa Sản Phẩm Flash Sale' : `Thêm Sản Phẩm (${products.length + 1}/${MAX_PRODUCTS_PER_CAMPAIGN})`}
                </h3>
                {!isEditingProduct && (
                  <p className="text-xs text-slate-400 mt-1">
                    Chiến dịch: <span className="text-amber-400 font-bold">{selectedCampaign?.title}</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleAddProduct} className="space-y-4">
                {/* Chọn sản phẩm */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">
                    Chọn sản phẩm gốc từ kho hàng
                  </label>
                  <select
                    required
                    value={productForm.productId || ''}
                    onChange={handleProductSelectChange}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition cursor-pointer"
                  >
                    <option value="">-- Chọn sản phẩm --</option>
                    {Array.isArray(allProducts) && allProducts.map((p) => {
                      const stockVal = p.stock !== undefined ? p.stock : (p.stock_quantity || 0);
                      const pId = p.realId || p.productId || p.id;
                      return (
                        <option key={pId} value={pId}>
                          [Kho: {stockVal}] {p.name} — {formatVND(p.price || 0)}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Tồn kho và Số lượng Sale */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tồn kho hiện tại</label>
                    <input
                      type="text"
                      readOnly
                      value={`${productForm.maxStock} sản phẩm`}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl p-3.5 text-xs text-emerald-400 font-bold cursor-not-allowed font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Số lượng đưa đi Sale</label>
                    <input
                      type="number"
                      required
                      min="1"
                      max={productForm.maxStock}
                      value={productForm.stockLimit}
                      onChange={(e) => setProductForm({ ...productForm, stockLimit: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition font-mono"
                    />
                  </div>
                </div>

                {/* Tên sản phẩm */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tên sản phẩm</label>
                  <input
                    type="text"
                    required
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition"
                  />
                </div>

                {/* Giá Flash Sale và Giá gốc */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Giá Flash Sale (VNĐ)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={productForm.flashSalePrice}
                      onChange={(e) => setProductForm({ ...productForm, flashSalePrice: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Giá gốc (VNĐ)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={productForm.originalPrice}
                      onChange={(e) => setProductForm({ ...productForm, originalPrice: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition font-mono"
                    />
                  </div>
                </div>

                {/* Nút hành động */}
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => { setShowProductModal(false); resetProductForm(); }}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold transition cursor-pointer text-slate-300"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-emerald-600/30"
                  >
                    {isEditingProduct ? 'Cập Nhật Thay Đổi' : 'Thêm Vào Flash Sale'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}